import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Shield, ExternalLink, Send, User, Mail, CheckCircle, Clock, AlertTriangle } from 'lucide-react';

const Relay = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [councillors, setCouncillors] = useState([]);
  const [selectedCouncillor, setSelectedCouncillor] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showMessageForm, setShowMessageForm] = useState(false);

  useEffect(() => {
    fetchCouncillors();
  }, []);

  const fetchCouncillors = async () => {
    try {
      const response = await api.get('/councillors');
      setCouncillors(response.data);
    } catch (error) {
      console.error('Error fetching councillors:', error);
      toast.error('Failed to load councillors');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCouncillor = (councillor) => {
    if (!user) {
      toast.error('Please log in to use the Relay Gateway');
      navigate('/login');
      return;
    }
    setSelectedCouncillor(councillor);
    setShowMessageForm(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!user) {
      toast.error('Please log in to send messages');
      navigate('/login');
      return;
    }

    if (!user.is_verified) {
      toast.error('Please verify your email before sending messages');
      return;
    }

    if (message.length < 20) {
      toast.error('Message must be at least 20 characters');
      return;
    }

    setSending(true);

    try {
      await api.post('/messages', {
        councillor_id: selectedCouncillor.id,
        message: message,
      });

      toast.success('Message relayed successfully!');
      setMessage('');
      setShowMessageForm(false);
      setSelectedCouncillor(null);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to send message';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  // Calculate responsiveness score
  const getResponsivenessDisplay = (councillor) => {
    const delivered = councillor.emails_delivered || 0;
    const responded = councillor.emails_responded || 0;
    const redemption = councillor.emails_redemption || 0;
    
    if (delivered === 0) return { label: 'NEW', color: 'gray' };
    
    const responseRate = ((responded + redemption) / delivered) * 100;
    
    if (responseRate >= 80) return { label: 'RESPONSIVE', color: 'green' };
    if (responseRate >= 50) return { label: 'MODERATE', color: 'yellow' };
    if (responseRate >= 20) return { label: 'SLOW', color: 'orange' };
    return { label: 'UNRESPONSIVE', color: 'red' };
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Sponsor Banner */}
        <div className="mb-6 flex justify-end">
          <a
            href="https://siporsplash.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#111] border border-[#2a2a2a] rounded px-3 py-2 hover:border-[#C5A52A] transition-colors"
          >
            <span className="font-mono text-[10px] text-gray-500 tracking-wider">SUPPORTED BY</span>
            <span className="font-display text-sm text-[#C5A52A] tracking-wider">SIPORSPLASH.CA</span>
          </a>
        </div>

        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="font-headline text-4xl md:text-5xl lg:text-6xl text-white mb-2">
            RELAY GATEWAY
          </h1>
          <p className="font-mono text-xs text-gray-500 tracking-[0.2em] uppercase">
            Secure Anonymous Discourse Bridge
          </p>
        </div>

        {/* Security Protocol */}
        <div className="bg-[#111] border border-[#1a1a1a] rounded p-6 mb-8">
          <div className="flex items-start gap-3 mb-4">
            <Shield className="w-5 h-5 text-green-500 flex-shrink-0 mt-1" />
            <h2 className="font-display text-lg text-white tracking-wider">
              SECURITY PROTOCOL
            </h2>
          </div>
          
          <ul className="space-y-3 text-gray-400 font-mono text-sm">
            <li className="flex items-start gap-2">
              <span className="text-green-500">•</span>
              <span>This service allows users to send their email directly to the councillor.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">•</span>
              <span>Any response comes back through our service and is provided to you securely.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">•</span>
              <span>High-priority content may be posted publicly, but identifying information will never be provided.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-500">•</span>
              <span>We log all incoming and outgoing metadata for audit integrity.</span>
            </li>
          </ul>
          
          <div className="mt-6 pt-4 border-t border-[#2a2a2a]">
            <p className="font-display text-xs text-gray-500 tracking-wider">
              THE RELAY GATEWAY SERVICE IS PROVIDED BY <span className="text-white">ACCOUNTABILITY.WORKS</span>.
            </p>
          </div>
        </div>

        {/* Councillor Selection */}
        <div className="mb-4">
          <h3 className="font-mono text-xs text-gray-500 tracking-wider uppercase mb-4">
            1. ROUTE TO:
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#C5A52A] mx-auto"></div>
            <p className="text-gray-500 mt-4 font-mono text-sm">Loading councillors...</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-4">
            {councillors.map((councillor) => {
              const responsiveness = getResponsivenessDisplay(councillor);
              const isMayor = councillor.ward?.toLowerCase().includes('mayor');
              
              return (
                <button
                  key={councillor.id}
                  onClick={() => handleSelectCouncillor(councillor)}
                  className={`group relative bg-[#111] border border-[#2a2a2a] rounded-lg p-4 hover:border-[#C5A52A] transition-all ${
                    selectedCouncillor?.id === councillor.id ? 'border-[#C5A52A] bg-[#1a1a1a]' : ''
                  }`}
                  data-testid={`councillor-${councillor.id}`}
                >
                  {/* Profile Image */}
                  <div className="w-16 h-16 mx-auto mb-2 bg-[#0a0a0a] rounded-full overflow-hidden border-2 border-[#2a2a2a] group-hover:border-[#C5A52A] transition-colors">
                    {councillor.image_url ? (
                      <img
                        src={councillor.image_url}
                        alt={councillor.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* Name & Ward */}
                  <p className="font-display text-xs text-white text-center tracking-wider truncate">
                    {councillor.name?.split(' ')[0]?.toUpperCase()}
                  </p>
                  <p className="font-mono text-[10px] text-gray-500 text-center">
                    {isMayor ? 'MAYOR' : councillor.ward}
                  </p>
                  
                  {/* Responsiveness Indicator */}
                  <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full ${
                    responsiveness.color === 'green' ? 'bg-green-500' :
                    responsiveness.color === 'yellow' ? 'bg-yellow-500' :
                    responsiveness.color === 'orange' ? 'bg-orange-500' :
                    responsiveness.color === 'red' ? 'bg-red-500' :
                    'bg-gray-500'
                  }`} title={responsiveness.label} />
                </button>
              );
            })}
          </div>
        )}

        {/* Message Form Modal */}
        {showMessageForm && selectedCouncillor && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-[#0a0a0a] border border-[#2a2a2a] w-full max-w-lg rounded">
              {/* Modal Header */}
              <div className="p-6 border-b border-[#2a2a2a]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#111] rounded-full overflow-hidden border border-[#2a2a2a]">
                      {selectedCouncillor.image_url ? (
                        <img
                          src={selectedCouncillor.image_url}
                          alt={selectedCouncillor.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-6 h-6 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="font-display text-lg text-white tracking-wider">
                        {selectedCouncillor.name?.toUpperCase()}
                      </h3>
                      <p className="font-mono text-xs text-gray-500">
                        {selectedCouncillor.ward}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowMessageForm(false);
                      setSelectedCouncillor(null);
                    }}
                    className="text-gray-500 hover:text-white text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Responsiveness Stats */}
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <div className="bg-[#111] rounded p-2 text-center">
                    <Mail className="w-4 h-4 text-gray-500 mx-auto mb-1" />
                    <p className="font-mono text-xs text-white">
                      {selectedCouncillor.emails_delivered || 0}
                    </p>
                    <p className="font-mono text-[10px] text-gray-600">DELIVERED</p>
                  </div>
                  <div className="bg-[#111] rounded p-2 text-center">
                    <CheckCircle className="w-4 h-4 text-green-500 mx-auto mb-1" />
                    <p className="font-mono text-xs text-white">
                      {selectedCouncillor.emails_responded || 0}
                    </p>
                    <p className="font-mono text-[10px] text-gray-600">RESPONDED</p>
                  </div>
                  <div className="bg-[#111] rounded p-2 text-center">
                    <Clock className="w-4 h-4 text-yellow-500 mx-auto mb-1" />
                    <p className="font-mono text-xs text-white">
                      {selectedCouncillor.emails_redemption || 0}
                    </p>
                    <p className="font-mono text-[10px] text-gray-600">REDEMPTION</p>
                  </div>
                </div>
              </div>

              {/* Message Form */}
              <form onSubmit={handleSendMessage} className="p-6">
                <label className="block font-mono text-xs text-[#C5A52A] tracking-wider mb-2 uppercase">
                  Your Message
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  rows={6}
                  className="w-full px-4 py-3 bg-[#111] border border-[#2a2a2a] text-white font-mono text-sm focus:border-[#C5A52A] focus:outline-none resize-none transition-colors"
                  placeholder="Write your message to the councillor..."
                />
                <p className="font-mono text-[10px] text-gray-600 mt-2">
                  Your identity will remain anonymous. The councillor will receive your message through our secure relay.
                </p>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowMessageForm(false);
                      setSelectedCouncillor(null);
                    }}
                    className="flex-1 bg-[#1a1a1a] hover:bg-[#2a2a2a] text-white font-display text-sm py-3 tracking-wider transition-colors"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    disabled={sending}
                    className="flex-1 bg-[#C5A52A] hover:bg-[#D4B93A] disabled:bg-[#C5A52A]/50 text-black font-display text-sm py-3 tracking-wider transition-colors flex items-center justify-center gap-2"
                    data-testid="send-relay-btn"
                  >
                    <Send className="w-4 h-4" />
                    {sending ? 'RELAYING...' : 'RELAY MESSAGE'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Relay;
